// Copyright 2023 Sorama B.V.

import { customElement, TaskQueue } from "aurelia-framework";
import { Reportable, ReportableMeasurement } from "datatypes/ReportableMeasurement";
import { SorXMeasurementTypeEnum } from "datatypes/SoramaWebApi";
import { SorXMeasurement } from "datatypes/SorXMeasurement";
import html2canvas from "html2canvas";
import html2pdf from "html2pdf.js";
import jsPDF from "jspdf";
import { FileDownloader } from "libraries/FileDownloader/FileDownloader";
import { MeasurementRepository } from "repositories/MeasurementRepository";
import { ProjectManagementService } from "services/Project/ProjectManagementService";
import { v4 as uuidv4 } from "uuid";

export enum SorxReportTypeEnum {
    LD = "SORXLD",
    PDD = "SORXPDD",
}

export const MeasurementTypePageCount: Record<SorXMeasurementTypeEnum, number> = {
    [SorXMeasurementTypeEnum.LeakDetection]: 1,
    [SorXMeasurementTypeEnum.PartialDischarge]: 2,
    [SorXMeasurementTypeEnum.Image]: 1,
    [SorXMeasurementTypeEnum.Video]: 1,
    [SorXMeasurementTypeEnum.MechanicalInspection]: 1,
};

@customElement("special-measurement-report-generator")
export class SpecialMeasurementReportGenerator {
    // Used to preview report during debugging
    // NOTE: When clicking download on the preview, the report styling WILL BE DIFFERENT (for unknown reasons) than downloading without preview
    // Also page numbering will not be correct
    private previewEnabled = false;

    private pageNumber = 1;
    private maxPage = 1;
    private overviewMaxPage = 1;
    private reportListChunk: Reportable[] = [];
    private reportList: Reportable[] = [];
    private readonly OVERVIEW_PAGE_SIZE = 18;

    private listReportType: SorxReportTypeEnum.LD | SorxReportTypeEnum.PDD | undefined;

    constructor(
        private readonly measurementRepository: MeasurementRepository,
        private readonly projectManagementService: ProjectManagementService,
        private readonly taskQueue: TaskQueue
    ) {}

    public async generatePdf(report: Reportable): Promise<Blob> {
        if (report.id === undefined) {
            report.id = uuidv4();
        }

        this.maxPage = MeasurementTypePageCount[(report as ReportableMeasurement<SorXMeasurement>).data.type];
        this.pageNumber = 1;

        this.reportListChunk.push(report);
        this.reportList.push(report);

        if (this.previewEnabled) return;

        // we need to wait for the document to be drawn/attached to the screen (also images need to be downloaded)
        await this.waitForRender();

        return this.htmlToPdf(report);
    }

    private async htmlToPdf(report: Reportable): Promise<Blob> {
        const element = document.getElementById("element-to-print-" + report.id);
        if (!element) throw new Error("Element not found");

        const opt = {
            margin: 0,
            filename: "Sorama-report.pdf",
            image: { type: "jpeg" as const, quality: 0.95 },
            html2canvas: { scale: 2 },
            pagebreak: { mode: "avoid-all", after: ".report-A4" },
            jsPDF: { unit: "in", format: "a4", orientation: "portrait" as const },
        };

        const worker = html2pdf().from(element).set(opt).toPdf();

        const blob = await worker.output("blob");

        // Open in new tab
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");

        this.removeFromList(report);

        return blob;
    }

    public async downloadPdf(report: Reportable) {
        await this.generatePdf(report).then((blob) =>
            FileDownloader.downloadBlobAsFile(blob, null, report.name + ".pdf")
        );
    }

    private updateListReportType() {
        //If the list is empty, return undefined
        if (!this.reportList || this.reportList.length === 0) {
            this.listReportType = undefined;
            return;
        }
        const allLD = this.reportList.every(
            (x) => (x as ReportableMeasurement<SorXMeasurement>).data.type === SorXMeasurementTypeEnum.LeakDetection
        );
        this.listReportType = allLD ? SorxReportTypeEnum.LD : SorxReportTypeEnum.PDD;
    }

    public async generatePdfFromMultiple(reports: Reportable[]): Promise<Blob> {
        const doc = new jsPDF({ unit: "in", format: "a4", orientation: "portrait" });

        const overviewPages = Math.ceil(reports.length / this.OVERVIEW_PAGE_SIZE);
        this.maxPage =
            overviewPages +
            reports.reduce(
                (acc, report) =>
                    acc + MeasurementTypePageCount[(report as ReportableMeasurement<SorXMeasurement>).data.type],
                0
            );

        await this.prepareReports(reports);
        await this.waitForRender();
        if (this.previewEnabled) return;

        let overviewPageNumber = Math.ceil(reports.length / this.OVERVIEW_PAGE_SIZE);
        this.overviewMaxPage = overviewPageNumber;
        this.reportList = reports;
        // overview pages based on reports page limit
        const overviewChunks = this.chunkReports(reports, this.OVERVIEW_PAGE_SIZE);
        for (let i = 0; i < overviewChunks.length; i++) {
            this.reportListChunk = [...overviewChunks[i]];
            this.pageNumber = i + 1;
            await this.waitForRender();

            if (i > 0) {
                doc.addPage(); // move to next page BEFORE rendering
            }

            await this.renderOverviewPage(doc);
        }
        this.pageNumber = overviewChunks.length + 1;

        //Individual report pages
        for (const report of reports) {
            const pages = MeasurementTypePageCount[(report as ReportableMeasurement<SorXMeasurement>).data.type];

            await this.renderIndividualReportPage(report, doc, this.pageNumber, this.maxPage);
            this.pageNumber += pages;
        }

        const blob = doc.output("blob");
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, "_blank");

        return blob;
    }

    private async prepareReports(reports: Reportable[]) {
        // Clean up
        this.reportListChunk = [];
        this.reportList = [];
        this.listReportType = undefined;

        await document.fonts.ready;
        await this.waitForRender();

        for (const report of reports) {
            if (!report.id) report.id = uuidv4();
            this.reportList.push(report);
        }

        this.updateListReportType();
    }

    private async renderOverviewPage(doc: jsPDF) {
        const summaryElement = document.getElementById("overview-element-to-print");
        if (!summaryElement) {
            console.warn("Summary template not found.");
            return;
        }

        await document.fonts.ready;
        await this.waitForRender();

        const canvas = await html2canvas(summaryElement, { scale: 2 });
        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const { width, height } = this.getScaledDimensions(doc, canvas);

        doc.addImage(imgData, "JPEG", 0, 0, width, height);
    }

    private async renderIndividualReportPage(report: Reportable, doc: jsPDF, pageNumber: number, maxPage: number) {
        if (!report.id) report.id = uuidv4();

        this.maxPage = maxPage;
        this.pageNumber = pageNumber;

        await document.fonts.ready;
        await this.waitForRender();

        const type = (report as ReportableMeasurement<SorXMeasurement>).data.type;
        if (type === SorXMeasurementTypeEnum.PartialDischarge) {
            await this.renderPageByElementId(`element-to-print-${report.id}-main`, doc);
            await this.renderPageByElementId(`element-to-print-${report.id}-plot`, doc);
        } else {
            await this.renderPageByElementId(`element-to-print-${report.id}`, doc);
        }
    }

    private async renderPageByElementId(elementId: string, doc: jsPDF) {
        const element = document.getElementById(elementId);
        if (!element) {
            console.warn("Element not found:", elementId);
            return;
        }
        await this.waitForRender();
        const canvas = await html2canvas(element, { scale: 2 });
        const imgData = canvas.toDataURL("image/jpeg", 0.95);
        const { width, height } = this.getScaledDimensions(doc, canvas);

        doc.addPage();
        doc.addImage(imgData, "JPEG", 0, 0, width, height);
    }

    private getScaledDimensions(doc: jsPDF, canvas: HTMLCanvasElement) {
        const imgProps = doc.getImageProperties(canvas.toDataURL("image/jpeg"));
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;

        return { width: pdfWidth, height: pdfHeight };
    }

    public async downloadCombinedPdf(reports: Reportable[]): Promise<void> {
        if (reports.length === 1) {
            this.downloadPdf(reports[0]);
            return;
        }

        const blob = await this.generatePdfFromMultiple(reports);
        FileDownloader.downloadBlobAsFile(blob, null, "Combined-Sorama-Report.pdf");
    }

    private removeFromList(report: Reportable) {
        this.reportListChunk.splice(
            this.reportListChunk.findIndex((x) => x.id === report.id),
            1
        );
    }

    private async waitForRender(): Promise<void> {
        return new Promise((resolve) => {
            // Let Aurelia microtasks run
            this.taskQueue.queueMicroTask(() => {
                // Let Aurelia tasks run
                this.taskQueue.queueTask(() => {
                    // Let browser paint the updated DOM
                    requestAnimationFrame(() => {
                        resolve();
                    });
                });
            });
        });
    }

    private chunkReports<T>(items: T[], size: number): T[][] {
        const chunks: T[][] = [];
        for (let i = 0; i < items.length; i += size) {
            chunks.push(items.slice(i, i + size));
        }
        return chunks;
    }

    protected closePreview() {
        this.reportListChunk = [];
    }

    protected downloadPreview() {
        const report = this.reportListChunk[0];
        this.htmlToPdf(report).then((blob) => FileDownloader.downloadBlobAsFile(blob, null, report.name + ".pdf"));
    }
}
